import { useTheme } from '@mui/material';
import { format } from 'date-fns';
import { Box, Typography, Grid, Divider } from '@mui/material';

interface SaleRow {
  id: string;
  customer_name: string;
  total_amount: number;
  created_at: string;
  users: { email: string } | null;
  discount: number;
  notes: string;
  items?: SaleItem[];
}

interface SaleItem {
  id: string;
  item_id: string;
  quantity: number;
  price_at_sale: number;
  discount: number;
  items: {
    name: string;
    description: string;
  };
}

interface CompanyInfo {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  website: string;
  logo?: string;
}

export const InvoicePrintComponent = ({ sales, companyInfo }: { sales: SaleRow[], companyInfo: CompanyInfo }) => {
  const theme = useTheme();
  const textColor = theme.palette.mode === 'dark' ? '#fff' : '#000';

  return (
    <Box sx={{ p: 0 }}>
      {sales.map((sale) => (
        <Box key={sale.id} sx={{ mb: 6, pageBreakAfter: 'always' }}>
          <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'center', sm: 'flex-start' }, gap: { xs: 3, sm: 0 } }}>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                INVOICE
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Invoice #: <strong>{sale.id}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Date: <strong>{format(new Date(sale.created_at), 'MMMM dd, yyyy')}</strong>
              </Typography>
            </Box>
            <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
              {companyInfo.logo && (
                <img 
                  src={companyInfo.logo} 
                  alt={companyInfo.name} 
                  style={{ height: 50, marginBottom: 8 }} 
                />
              )}
              <Typography variant="h6" fontWeight={600}>{companyInfo.name}</Typography>
              <Typography variant="body2">{companyInfo.address}</Typography>
              <Typography variant="body2">{companyInfo.city}, {companyInfo.state} {companyInfo.zip}</Typography>
              <Typography variant="body2">{companyInfo.phone}</Typography>
              <Typography variant="body2">{companyInfo.email}</Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />
          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                BILL TO
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                {sale.customer_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                PAYMENT DETAILS
              </Typography>
              <Typography variant="body2">
                <strong>Payment Method:</strong> Credit Card
              </Typography>
              <Typography variant="body2">
                <strong>Transaction ID:</strong> {sale.id}
              </Typography>
              <Typography variant="body2">
                <strong>Employee:</strong> {sale.users?.email?.split('@')[0] || 'Unknown'}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ 
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
            borderRadius: 1,
            overflow: 'auto',
            mb: 4,
            width: '100%'
          }}>
            <Box sx={{ minWidth: 600, overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                color: textColor,
              }}>
                <thead>
                  <tr style={{ 
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.03)',
                  }}>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Item</th>
                    <th style={{ textAlign: 'center', padding: '12px 16px', fontWeight: 600 }}>Quantity</th>
                    <th style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 600 }}>Unit Price</th>
                    <th style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 600 }}>Discount</th>
                    <th style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 600 }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {sale.items?.map((item) => {
                    const itemTotal = item.quantity * item.price_at_sale * (1 - item.discount / 100);
                    
                    return (
                      <tr
                        key={item.id}
                        style={{ 
                          borderTop: '1px solid',
                          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                        }}
                      >
                        <td style={{ padding: '12px 16px' }}>
                          <Typography variant="body2" fontWeight={500}>{item.items.name}</Typography>
                          {item.items.description && (
                            <Typography variant="caption" color="text.secondary">
                              {item.items.description}
                            </Typography>
                          )}
                        </td>
                        <td style={{ textAlign: 'center', padding: '12px 16px' }}>
                          {item.quantity}
                        </td>
                        <td style={{ textAlign: 'right', padding: '12px 16px' }}>
                          ₹{item.price_at_sale.toFixed(2)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '12px 16px' }}>
                          {item.discount > 0 ? `${item.discount}%` : '-'}
                        </td>
                        <td style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 500 }}>
                          ₹{itemTotal.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td colSpan={3} style={{ padding: '12px 16px' }}></td>
                    <td style={{ 
                      textAlign: 'right', 
                      padding: '12px 16px',
                      borderTop: '1px solid',
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                      fontWeight: 600
                    }}>
                      Subtotal:
                    </td>
                    <td style={{ 
                      textAlign: 'right', 
                      padding: '12px 16px',
                      borderTop: '1px solid',
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                      fontWeight: 600
                    }}>
                      ₹{sale.total_amount.toFixed(2)}
                    </td>
                  </tr>
                  {sale.discount > 0 && (
                    <tr>
                      <td colSpan={3}></td>
                      <td style={{ textAlign: 'right', padding: '4px 16px' }}>
                        Sale Discount:
                      </td>
                      <td style={{ textAlign: 'right', padding: '4px 16px' }}>
                        {sale.discount}%
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan={3}></td>
                    <td style={{ 
                      textAlign: 'right', 
                      padding: '16px',
                      fontWeight: 700,
                      fontSize: '1.1em',
                      borderTop: '2px solid',
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                    }}>
                      TOTAL:
                    </td>
                    <td style={{ 
                      textAlign: 'right', 
                      padding: '16px',
                      fontWeight: 700,
                      fontSize: '1.1em',
                      borderTop: '2px solid',
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                    }}>
                      ₹{sale.total_amount.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              NOTES
            </Typography>
            <Box sx={{ 
              p: 2, 
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              borderRadius: 1 
            }}>
              <Typography variant="body2">
                {sale.notes || "No additional notes for this invoice."}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" gutterBottom>
              Thank you for your business!
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This invoice was generated on {format(new Date(), 'MMMM dd, yyyy, HH:mm')}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
